import React, { useRef } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Auth0Provider } from "@auth0/auth0-react";
import {
  ConfigurationContext,
  ConfigurationProvider,
} from "./utils/ConfigurationProvider";
import { PostHogProvider } from "posthog-js/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "./api/queryClient";
import "./index.css";
import { initializeConsoleOverride } from "./utils/consoleOverride";

const rootElement = document.getElementById("root");
const root = createRoot(rootElement);

// Initialize console override early in the app lifecycle
initializeConsoleOverride();

const Auth0ProviderWithRedirectCallback = ({
  children,
  onLoginCallback,
  ...props
}) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState, user) => {
    if (onLoginCallback) {
      onLoginCallback(appState, user);
    }
    navigate((appState && appState.returnTo) || window.location.pathname);
  };

  return (
    <Auth0Provider onRedirectCallback={onRedirectCallback} {...props}>
      {children}
    </Auth0Provider>
  );
};

const AppWrapper = ({ config }) => {
  const appRef = useRef();

  const auth0Provider = (
    <Auth0ProviderWithRedirectCallback
      domain={config.authDomain}
      clientId={config.authClientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: config.authAudience,
        scope: "access profile",
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      onLoginCallback={() => {
        if (appRef.current) {
          appRef.current.onLogin();
        }
      }}
    >
      <App ref={appRef} />
    </Auth0ProviderWithRedirectCallback>
  );

  if (config.posthogPublicApiKey) {
    return (
      <PostHogProvider
        apiKey={config.posthogPublicApiKey}
        options={{
          api_host: "https://r.ontik.io",
          ui_host: "https://eu.posthog.com",
          capture_pageview: "history_change",
          person_profiles: "always",
        }}
      >
        {auth0Provider}
      </PostHogProvider>
    );
  }

  return auth0Provider;
};

root.render(
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ConfigurationProvider>
        <ConfigurationContext.Consumer>
          {({ config }) =>
            config ? <AppWrapper config={config} /> : <span></span>
          }
        </ConfigurationContext.Consumer>
      </ConfigurationProvider>
    </BrowserRouter>
    {/* Temporarily hidden — restore to bring back the React Query devtools button. */}
    {/* {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />} */}
  </QueryClientProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
